import { fetchApi } from '../client';
import type { PagedResponse } from '../../../types/pagination';

export interface MonthTrend {
  month: string;
  label: string;
  revenue: number;
  consultations: number;
}

export interface RevenueTrendResponse {
  months: MonthTrend[];
  forecastNextMonth: number;
  forecastLabel: string;
}

export interface DoctorEfficiencyRow {
  doctorId: string;
  doctorName: string;
  totalRevenue: number;
  consultations: number;
  revenuePerConsult: number;
}

export interface LostRevenueResponse {
  noShows: { count: number; estimatedLoss: number };
  overdueFollowUps: { count: number; estimatedLoss: number };
  unpaidBills: { count: number; totalAmount: number };
  totalEstimatedLoss: number;
  averageBillValue: number;
  periodDays: number;
}

export interface PatientCohortResponse {
  newPatients: { count: number; revenue: number };
  returningPatients: { count: number; revenue: number };
  totalRevenue: number;
  newPct: number;
  periodDays: number;
}

export interface OutstandingBill {
  billId: string;
  billNumber: string;
  patientId: string;
  patientName: string;
  billDate: string;
  daysOld: number;
  outstanding: number;
  bucket: '0-30d' | '31-60d' | '61-90d' | '90d+';
}

export interface OutstandingResponse {
  bills: OutstandingBill[];
  /** Number of unpaid bills with a positive balance (all of them, not just the listed rows). */
  totalPending: number;
  /** True when `bills` lists only the oldest subset; agingBuckets still cover every bill. */
  truncated?: boolean;
  agingBuckets: Record<string, number>;
}

/** Paged shape (medopd-docs 117): `bills` is one page of every outstanding bill, oldest first. */
export interface OutstandingPageResponse extends Omit<OutstandingResponse, 'bills'> {
  bills: PagedResponse<OutstandingBill>;
}

export interface SuggestionFinding {
  ruleCode: string;
  severity: 'HIGH' | 'MODERATE' | 'LOW';
  title: string;
  message: string;
  estimatedMonthlyImpact: number;
}

const range = (from?: string, to?: string) =>
  from && to ? `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}` : '';

export const revenueIntelligenceModule = {
  getTrend: () =>
    fetchApi<RevenueTrendResponse>('/api/analytics/revenue/trend'),

  /** from/to (yyyy-MM-dd, inclusive) are optional: the server defaults to the last 90 / 30 / 90 days (medopd-docs 136 §3). */
  getDoctorEfficiency: (from?: string, to?: string) =>
    fetchApi<{ doctors: DoctorEfficiencyRow[]; periodDays: number }>(`/api/analytics/revenue/doctor-efficiency${range(from, to)}`),

  getLostRevenue: (from?: string, to?: string) =>
    fetchApi<LostRevenueResponse>(`/api/analytics/revenue/lost-revenue${range(from, to)}`),

  getPatientCohort: (from?: string, to?: string) =>
    fetchApi<PatientCohortResponse>(`/api/analytics/revenue/patient-cohort${range(from, to)}`),

  getOutstanding: () =>
    fetchApi<OutstandingResponse>('/api/analytics/revenue/outstanding'),

  /** Aging totals over every unpaid bill plus one page of the bill list. */
  getOutstandingPage: (page: number, size: number) =>
    fetchApi<OutstandingPageResponse>(`/api/analytics/revenue/outstanding?page=${page}&size=${size}`),

  getSuggestions: () =>
    fetchApi<{ suggestions: SuggestionFinding[] }>('/api/analytics/revenue/suggestions'),
};
